import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import { ResponsiveLayoutRow } from 'apps/fleet-planner/components/layout/ResponsiveLayoutRow'
import { TeamMemberTableFilters } from 'apps/fleet-planner/components/team/TeamMemberTableFilters'
import { TeamMembersList } from 'apps/fleet-planner/components/team/TeamMembersList'
import { TeamMembersTable } from 'apps/fleet-planner/components/team/TeamMembersTable'
import { useTeamMemberContext } from 'apps/fleet-planner/context/TeamMemberContext'
import { ITeamMember, TEAM_MEMBER_ROLE_TYPES } from 'apps/fleet-planner/types'
import { AddIcon, AddPersonIcon } from 'transfix-icons'

interface ITeamMembersPageProps {
  /**
   * The update team member id, so we can highlight the row
   */
  lastUpdatedTeamMemberId: number | undefined
  /**
   * A function to be executed when the edit button is clicked
   */
  onTeamMemberEdit?: (teamMember?: ITeamMember) => void
}

export const TeamMembersPage: FC<ITeamMembersPageProps> = ({
  lastUpdatedTeamMemberId,
  onTeamMemberEdit,
}) => {
  const history = useHistory()
  const { isMobile } = useBreakpointContext()
  const {
    currentTeamMember,
    filteredTeamMembers,
    isFetching,
    setModalMode,
    setSelectedTeamMember,
  } = useTeamMemberContext()

  const primaryActionClickHandler = () => {
    setSelectedTeamMember(undefined)
    setModalMode('add_new')
  }

  const teamMemberClickHandler = (teamMember: ITeamMember) => {
    const { id } = teamMember || {}
    history.push(`/team/member/${id}/view`)
    setSelectedTeamMember(teamMember)
    setModalMode(undefined)
  }

  const teamMemberEditHandler = (teamMember?: ITeamMember) => {
    if (onTeamMemberEdit && teamMember) {
      onTeamMemberEdit(teamMember)
    }
  }

  const { motorCarrierDetails: carriers } = currentTeamMember || {}
  const isUserAnAdminForACarrier = carriers?.some(carrier => {
    const { role } = carrier || {}
    return role === TEAM_MEMBER_ROLE_TYPES.ADMIN
  })

  return (
    <ResponsiveLayout
      title='Team'
      primaryActionProps={
        isUserAnAdminForACarrier
          ? {
              label: 'Add Team Member',
              buttonIcon: AddPersonIcon,
              fabIcon: AddIcon,
              desktopButtonProps: {
                disabled: isFetching,
              },
              onClick: primaryActionClickHandler,
            }
          : undefined
      }>
      <ResponsiveLayoutRow>
        <TeamMemberTableFilters />
      </ResponsiveLayoutRow>

      {isMobile ? (
        <ResponsiveLayoutRow fluid>
          <TeamMembersList
            isLoading={isFetching}
            teamMembers={filteredTeamMembers}
            onTeamMemberClick={teamMemberClickHandler}
          />
        </ResponsiveLayoutRow>
      ) : (
        <ResponsiveLayoutRow BoxProps={{ pt: 2.5, px: 4, pb: 7 }}>
          <TeamMembersTable
            isLoading={isFetching}
            teamMembers={filteredTeamMembers}
            onIsRowUpdated={teamMember => teamMember?.id === lastUpdatedTeamMemberId}
            onTeamMemberEdit={teamMemberEditHandler}
          />
        </ResponsiveLayoutRow>
      )}
    </ResponsiveLayout>
  )
}
